// ArticleReader.tsx — the single-article ("beat") reader, opened as an overlay
// from the Library. Ports the source app's lightweight markdown renderer
// (paragraphs, **bold**, - bullets, > quotes, **Subhead**) onto this app's tokens.
import React from 'react';
import { Display } from '@/components/ui';
import { Icon } from '@/components/icons';
import { getEntryById, markRead, isSaved, toggleSaved, categoryKicker, pillarColor } from '@/services/library';
import { ThemeMotif } from '@/flows/library/ThemeMotif';
import { LessonAudioPlayer } from '@/components/LessonAudioPlayer';
import libraryAudio from '@/data/libraryAudio.json';
import { useProgress } from '@/hooks/useProgress';

// article id → narration CDN url (generated by scripts/upload-library-audio-bunny.cjs).
const LIBRARY_AUDIO = libraryAudio as Record<string, string>;

function inlineFormat(text: string, keyBase: string): React.ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    /^\*\*[^*]+\*\*$/.test(p) ? (
      <strong key={`${keyBase}-${i}`} style={{ fontWeight: 700 }}>
        {p.slice(2, -2)}
      </strong>
    ) : (
      <span key={`${keyBase}-${i}`}>{p}</span>
    ),
  );
}

function renderBody(body: string, scale = 1, accent = 'var(--amber-deep)'): React.ReactNode[] {
  const s = (px: number) => Math.round(px * scale * 10) / 10;
  const blocks = body
    .split(/\n{2,}/)
    .map((b) => b.trim())
    .filter(Boolean);
  // The first paragraph-type block becomes the lede (larger, lighter, drop-cap).
  let ledeUsed = false;
  return blocks.map((block, i) => {
    const key = `b${i}`;
    // standalone bold subhead — a landmark with an accent mark above it that
    // doubles as the section divider (Phase 3 #4 + #6).
    if (/^\*\*[^*]+\*\*$/.test(block)) {
      return (
        <h3
          key={key}
          style={{
            fontFamily: 'Fraunces, Georgia, serif',
            fontSize: s(20),
            fontWeight: 600,
            letterSpacing: '0.005em',
            lineHeight: 1.22,
            margin: '38px 0 12px',
            color: 'var(--ink)',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              display: 'block',
              width: 28,
              height: 3,
              borderRadius: 2,
              background: accent,
              marginBottom: 12,
              opacity: 0.9,
            }}
          />
          {block.slice(2, -2)}
        </h3>
      );
    }
    // bullet list
    if (/^\s*-\s/.test(block.split('\n')[0])) {
      const items = block
        .split('\n')
        .filter((l) => /^\s*-\s/.test(l))
        .map((l) => l.replace(/^\s*-\s+/, ''));
      return (
        <ul key={key} style={{ margin: '8px 0 18px', padding: 0, listStyle: 'none' }}>
          {items.map((it, j) => (
            <li
              key={j}
              style={{
                position: 'relative',
                paddingLeft: 20,
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: s(16),
                lineHeight: 1.6,
                color: 'var(--ink)',
                marginBottom: 9,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: 4,
                  top: 11,
                  width: 5,
                  height: 5,
                  borderRadius: '50%',
                  background: accent,
                }}
              />
              {inlineFormat(it, key + j)}
            </li>
          ))}
        </ul>
      );
    }
    // blockquote → editorial pull-quote: large serif, generous margins, an
    // oversized opening mark in the category accent, set off from the body.
    if (/^\s*>\s?/.test(block.split('\n')[0])) {
      const lines = block.split('\n').map((l) => l.replace(/^\s*>\s?/, ''));
      return (
        <blockquote
          key={key}
          style={{
            position: 'relative',
            margin: '30px 0',
            padding: '4px 0 4px 26px',
            borderLeft: `2px solid ${accent}`,
            fontFamily: 'Fraunces, Georgia, serif',
            fontStyle: 'italic',
            fontWeight: 500,
            fontSize: s(21),
            lineHeight: 1.42,
            color: 'var(--ink)',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: 18,
              top: s(-6),
              fontFamily: 'Fraunces, Georgia, serif',
              fontSize: s(54),
              lineHeight: 1,
              fontStyle: 'normal',
              color: accent,
              opacity: 0.22,
              pointerEvents: 'none',
            }}
          >
            &ldquo;
          </span>
          {lines.map((line, k) => (
            <p key={k} style={{ margin: '0 0 6px' }}>
              {inlineFormat(line, key + k)}
            </p>
          ))}
        </blockquote>
      );
    }
    // paragraph (the first one is the lede)
    const isLede = !ledeUsed;
    ledeUsed = true;
    if (isLede) {
      return (
        <p
          key={key}
          className="article-lede"
          style={{
            fontFamily: 'Inter, system-ui, sans-serif',
            fontSize: s(19.5),
            lineHeight: 1.5,
            color: 'var(--ink-soft)',
            margin: '0 0 20px',
            // accent for the ::first-letter drop-cap (see theme.css)
            ['--cap' as string]: accent,
          } as React.CSSProperties}
        >
          {inlineFormat(block, key)}
        </p>
      );
    }
    return (
      <p
        key={key}
        style={{
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: s(16),
          lineHeight: 1.72,
          color: 'var(--ink)',
          margin: '0 0 18px',
        }}
      >
        {inlineFormat(block, key)}
      </p>
    );
  });
}

export function ArticleReader({ id, onClose }: { id: string; onClose: () => void }) {
  const entry = getEntryById(id);
  const fontScale = useProgress().state.fontScale;
  const [, force] = React.useReducer((x: number) => x + 1, 0);

  React.useEffect(() => {
    if (entry) markRead(entry.id);
  }, [entry]);

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 82,
        background: 'var(--cream)',
        display: 'flex',
        flexDirection: 'column',
        animation: 'rise .42s cubic-bezier(.2,.7,.3,1)',
      }}
    >
      <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <div style={{ position: 'relative', padding: 'calc(18px + var(--safe-top)) 22px calc(46px + var(--safe-bottom))', maxWidth: 640, margin: '0 auto' }}>
          {/* faint theme motif behind the kicker + title (right side only, below
              the back button). Orphan keys render nothing → Phase 1 tint only. */}
          {entry && (
            <ThemeMotif
              theme={entry.primaryShelf}
              opacity={0.07}
              style={{
                top: 'calc(58px + var(--safe-top))',
                left: 'auto',
                right: 0,
                width: '58%',
                height: 150,
                zIndex: 0,
              }}
            />
          )}
          {/* header row */}
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <button
              type="button"
              onClick={onClose}
              aria-label="Back to Library"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--ink-soft)',
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: 14,
                fontWeight: 600,
                padding: '4px 2px',
                minHeight: 40,
              }}
            >
              <Icon name="chevLeft" size={18} /> Library
            </button>
            {entry && (
              <button
                type="button"
                onClick={() => {
                  toggleSaved(entry.id);
                  force();
                }}
                aria-label={isSaved(entry.id) ? 'Remove from saved' : 'Save for later'}
                aria-pressed={isSaved(entry.id)}
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 999,
                  border: '1px solid var(--hair)',
                  background: 'linear-gradient(180deg, var(--card-top), var(--card-bot))',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: isSaved(entry.id) ? 'var(--amber)' : 'var(--ink-soft)',
                  boxShadow: 'var(--edge), var(--sh1)',
                  transition: 'transform .2s cubic-bezier(.2,.7,.3,1)',
                  transform: isSaved(entry.id) ? 'scale(1.06)' : 'scale(1)',
                }}
              >
                {/* the app's heart affordance: filled when saved (matches FavouriteButton) */}
                <svg viewBox="0 0 24 24" width={18} height={18} fill={isSaved(entry.id) ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: 'block' }}>
                  <path d="M12 19.5C6.5 16 4 12.7 4 9.5A3.8 3.8 0 0 1 12 7a3.8 3.8 0 0 1 8 2.5c0 3.2-2.5 6.5-8 10z" />
                </svg>
              </button>
            )}
          </div>

          {!entry ? (
            <p style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ink-soft)', marginTop: 40 }}>
              Article not found.
            </p>
          ) : (
            <>
              <div
                style={{
                  position: 'relative',
                  zIndex: 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  marginTop: 14,
                  padding: '4px 11px',
                  borderRadius: 999,
                  background: 'var(--card-top)',
                  border: '1px solid var(--hair)',
                  fontFamily: 'Inter, system-ui, sans-serif',
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  color: categoryKicker(entry),
                }}
              >
                {entry.primaryShelf}
                <span style={{ color: 'var(--ink-soft)', opacity: 0.7 }}>· {entry.duration}</span>
              </div>
              <Display parts={{ pre: entry.title, em: '' }} size={28} lh={1.18} style={{ position: 'relative', zIndex: 1, margin: '14px 0 18px' }} />
              {/* AI-narrated version — the same standard voice as the lessons. Optional:
                  the text stays; tap to listen. Honest provenance note beneath it. */}
              {LIBRARY_AUDIO[entry.id] && (
                <div style={{ position: 'relative', zIndex: 1, maxWidth: '34em', marginBottom: 20 }}>
                  <LessonAudioPlayer src={LIBRARY_AUDIO[entry.id]} />
                  <p style={{ margin: '4px 2px 0', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11.5, fontStyle: 'italic', lineHeight: 1.5, color: 'var(--ink-soft)' }}>
                    Written by a Psychologist, spoken by an AI voice.
                  </p>
                </div>
              )}
              {/* comfortable reading measure (~66 chars at base size) */}
              <div style={{ position: 'relative', zIndex: 1, maxWidth: '34em' }}>{renderBody(entry.body, fontScale, pillarColor(entry))}</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
